import React from 'react';
import { Button, Loader, cx } from '@dynamon-as/ui';
import { Edit, Drafts, Event } from '@mui/icons-material';
import type { Request } from '@features/requests/types';
import { REQUEST_TYPE_ICONS } from '@features/requests/constants';
import type { RequestTypeKey } from '@features/requests/utils';
import { formatDate, getRequestTypeKey } from '@features/requests/utils';
import { useRequestTranslations } from '@shared/i18n';
import { useI18n } from '@shared/i18n';

import styles from './DraftsList.module.css';

/**
 * Props for DraftsList component
 */
export interface DraftsListProps {
    /** Drafts to display (already filtered, sorted and limited) */
    drafts: Request[];
    /** Loading state while drafts are being fetched */
    isLoading?: boolean;
    /** Callback when user wants to edit a draft */
    onEdit: (id: string) => void;
}

/** Accent style per request type icon container */
const TYPE_ICON_CLASS: Record<RequestTypeKey, string> = {
    VACATION: styles.typeIconVacation,
    PAID_LEAVE: styles.typeIconPaidLeave,
    SHIFT_PREFERENCE: styles.typeIconShiftPreference,
};

/**
 * Displays a compact inline list of drafts (one draft per row),
 * each one with its type, deadline and a single edit action.
 */
export const DraftsList = React.memo(function DraftsList({
    drafts,
    isLoading = false,
    onEdit,
}: DraftsListProps): React.ReactElement {
    const { getRequestTypeLabel } = useRequestTranslations();
    const { t } = useI18n();

    return (
        <Loader
            isLoading={isLoading}
            variant="inline"
            label={t('requests.drafts.loading')}
        >
            {drafts.length === 0 ? (
                <div className={styles.empty}>
                    <Drafts className={styles.emptyIcon} />
                    <p className={styles.emptyMessage}>
                        {t('requests.drafts.emptyMessage')}
                    </p>
                </div>
            ) : (
                <ul className={styles.list}>
                    {drafts.map((draft): React.ReactElement => {
                        const typeKey = getRequestTypeKey(draft.type);
                        const TypeIcon = REQUEST_TYPE_ICONS[typeKey];
                        const typeLabel = getRequestTypeLabel(typeKey);

                        return (
                            <li
                                key={draft.id}
                                className={styles.item}
                                data-testid="draft-item"
                            >
                                <span
                                    className={cx(
                                        styles.typeIcon,
                                        TYPE_ICON_CLASS[typeKey],
                                    )}
                                >
                                    <TypeIcon fontSize="small" />
                                </span>
                                <span className={styles.body}>
                                    <span className={styles.type}>
                                        {typeLabel}
                                    </span>
                                    {draft.deadline && (
                                        <span className={styles.deadline}>
                                            <Event
                                                fontSize="small"
                                                className={styles.deadlineIcon}
                                            />
                                            {t('requests.deadline')}:{' '}
                                            {formatDate(draft.deadline)}
                                        </span>
                                    )}
                                </span>
                                <div className={styles.actions}>
                                    <Button
                                        Icon={Edit}
                                        color="secondary"
                                        variant="flat"
                                        rounded={true}
                                        size="small"
                                        onClick={(): void => onEdit(draft.id)}
                                        aria-label={t(
                                            'requests.drafts.editDraft',
                                            { type: typeLabel },
                                        )}
                                    />
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </Loader>
    );
});
